<template>
  <div class="payout-graph" ref="container">
    <svg ref="svg" :width="width" :height="height">
      <defs>
        <marker id="pg-arrow" markerWidth="8" markerHeight="6" refX="7" refY="3" orient="auto">
          <polygon points="0 0, 8 3, 0 6" fill="#888" />
        </marker>
        <marker id="pg-arrow-active" markerWidth="8" markerHeight="6" refX="7" refY="3" orient="auto">
          <polygon points="0 0, 8 3, 0 6" fill="#f0b90b" />
        </marker>
        <marker id="pg-arrow-ok" markerWidth="8" markerHeight="6" refX="7" refY="3" orient="auto">
          <polygon points="0 0, 8 3, 0 6" fill="#28a745" />
        </marker>
        <marker id="pg-arrow-fail" markerWidth="8" markerHeight="6" refX="7" refY="3" orient="auto">
          <polygon points="0 0, 8 3, 0 6" fill="#dc3545" />
        </marker>
        <filter id="pg-glow">
          <feGaussianBlur stdDeviation="4" result="blur"/>
          <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
        </filter>
      </defs>

      <!-- Edges -->
      <g class="edges">
        <g v-for="t in targets" :key="'e-' + t.id" class="edge-group"
           :class="'edge-' + t.status"
           @mouseenter="hoveredTarget = t.id"
           @mouseleave="hoveredTarget = null">
          <path :d="edgePath(t)" stroke-width="2" fill="none"
                :stroke="edgeColor(t)"
                :stroke-dasharray="t.status === 'active' ? '8 4' : 'none'"
                :marker-end="edgeMarker(t)"
                :class="{ 'edge-anim': t.status === 'active' }" />
          <text v-if="hoveredTarget === t.id || t.status === 'active'"
                :x="edgeMidX(t)" :y="edgeMidY(t) - 6"
                text-anchor="middle" fill="#ddd" font-size="11"
                class="edge-label">{{ t.amount }} USDT</text>
        </g>
      </g>

      <!-- Source node (center) -->
      <g v-if="sourceAddress" class="source-node"
         :transform="`translate(${cx},${cy})`">
        <circle r="40" :fill="sourceNodeFill" filter="url(#pg-glow)"
                :class="{ 'pulse': sourceAml && (sourceAml.hardBlocked || sourceAml.softBlocked) }" />
        <circle r="40" fill="none" stroke="#f0b90b" stroke-width="2.5" />
        <text y="-14" text-anchor="middle" fill="#f0b90b" font-size="11" font-weight="700">SOURCE</text>
        <text y="2" text-anchor="middle" fill="#fff" font-size="10">{{ shortAddr(sourceAddress) }}</text>
        <text y="16" text-anchor="middle" fill="#aaa" font-size="10">{{ sourceBalance }} USDT</text>
        <text v-if="sourceAml" y="30" text-anchor="middle"
              :fill="riskColor(sourceAml.risk_level)" font-size="9">
          {{ sourceAml.risk_level.toUpperCase() }}
        </text>
      </g>
      <g v-else class="source-node" :transform="`translate(${cx},${cy})`">
        <circle r="36" fill="#2a2a2a" stroke="#555" stroke-width="1.5" stroke-dasharray="6 3" />
        <text text-anchor="middle" fill="#666" font-size="11">No source</text>
      </g>

      <!-- Target nodes (radial) -->
      <g v-for="(t, i) in targets" :key="'n-' + t.id"
         class="target-node" :class="{ 'selected': selectedId === t.id }"
         :transform="`translate(${nodeX(i)},${nodeY(i)})`"
         style="cursor:pointer"
         @click="$emit('select', t.id)">
        <circle r="30" :fill="targetNodeFill(t)"
                :stroke="selectedId === t.id ? '#fff' : nodeStroke(t)"
                :stroke-width="selectedId === t.id ? 2.5 : 1.5" />
        <text y="-9" text-anchor="middle" fill="#ccc" font-size="9">{{ shortAddr(t.address) }}</text>
        <text y="4" text-anchor="middle" fill="#fff" font-size="10" font-weight="600">
          {{ t.amount || '—' }} USDT
        </text>
        <text v-if="t.usdtBalance" y="16" text-anchor="middle" fill="#888" font-size="9">
          bal: {{ t.usdtBalance }}
        </text>
        <text v-if="t.amlResult" y="26" text-anchor="middle"
              :fill="riskColor(t.amlResult.risk_level)" font-size="8">
          {{ t.amlResult.risk_level.toUpperCase() }}
        </text>
        <text v-if="statusIcon(t)" y="26" text-anchor="middle" font-size="13"
              style="dominant-baseline:central">{{ statusIcon(t) }}</text>
      </g>
    </svg>
    <div v-if="!sourceAddress && !targets.length" class="pg-empty">
      Add a source address and targets to see the payout graph
    </div>
  </div>
</template>

<script>
import { RISK_COLOR } from '../services/amlService';

export default {
  name: 'PayoutGraph',
  props: {
    sourceAddress: { type: String, default: '' },
    sourceBalance: { type: String, default: '0' },
    sourceAml:     { type: Object, default: null },
    targets:       { type: Array, default: () => [] },
    selectedId:    { type: String, default: null },
  },
  emits: ['select'],

  data() {
    return { width: 600, height: 420, hoveredTarget: null };
  },

  computed: {
    cx() { return this.width / 2; },
    cy() { return this.height / 2; },

    sourceNodeFill() {
      if (!this.sourceAml) return '#1a2f1a';
      const colors = { low: '#1a2f1a', medium: '#2f2a10', high: '#2f1a10', critical: '#2f1010', unknown: '#1a1a2f' };
      return colors[this.sourceAml.risk_level] || '#1a1a2f';
    },
  },

  mounted() { this.resize(); window.addEventListener('resize', this.resize); },
  beforeDestroy() { window.removeEventListener('resize', this.resize); },

  methods: {
    resize() {
      const el = this.$refs.container;
      if (el) { this.width = el.clientWidth || 600; this.height = el.clientHeight || 420; }
    },

    riskColor(level) { return RISK_COLOR[level] || RISK_COLOR.unknown; },

    shortAddr(addr) {
      if (!addr) return '—';
      return addr.length > 14 ? addr.slice(0, 7) + '…' + addr.slice(-5) : addr;
    },

    // Angle for target node i
    angle(i) {
      const n = this.targets.length || 1;
      return (2 * Math.PI * i) / n - Math.PI / 2;
    },

    radius() {
      const n = this.targets.length;
      const minR = 120;
      const perNode = n > 1 ? (2 * Math.PI * minR) / (n * 1.8) : 0;
      return Math.max(minR, Math.min(perNode * n / (2 * Math.PI), 180));
    },

    nodeX(i) { return this.cx + this.radius() * Math.cos(this.angle(i)); },
    nodeY(i) { return this.cy + this.radius() * Math.sin(this.angle(i)); },

    edgePath(t) {
      const i = this.targets.indexOf(t);
      const tx = this.nodeX(i);
      const ty = this.nodeY(i);
      const dx = tx - this.cx;
      const dy = ty - this.cy;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const srcR = 42;
      const dstR = 33;
      const sx = this.cx + (dx / dist) * srcR;
      const sy = this.cy + (dy / dist) * srcR;
      const ex = tx - (dx / dist) * dstR;
      const ey = ty - (dy / dist) * dstR;
      // slight quadratic curve
      const mx = (sx + ex) / 2 - dy * 0.18;
      const my = (sy + ey) / 2 + dx * 0.18;
      return `M${sx},${sy} Q${mx},${my} ${ex},${ey}`;
    },

    edgeMidX(t) {
      const i = this.targets.indexOf(t);
      return (this.cx + this.nodeX(i)) / 2;
    },
    edgeMidY(t) {
      const i = this.targets.indexOf(t);
      return (this.cy + this.nodeY(i)) / 2;
    },

    edgeColor(t) {
      const map = { confirmed: '#28a745', failed: '#dc3545', active: '#f0b90b', broadcasting: '#17a2b8', pending: '#555', blocked: '#dc3545' };
      return map[t.status] || '#555';
    },

    edgeMarker(t) {
      if (t.status === 'active') return 'url(#pg-arrow-active)';
      if (t.status === 'confirmed') return 'url(#pg-arrow-ok)';
      if (t.status === 'failed') return 'url(#pg-arrow-fail)';
      return 'url(#pg-arrow)';
    },

    targetNodeFill(t) {
      if (t.amlResult && (t.amlResult.hardBlocked || t.amlResult.softBlocked)) return '#2a1010';
      const map = { confirmed: '#0d2a15', failed: '#2a0d0d', active: '#2a2010', broadcasting: '#0d2a2a', blocked: '#2a0d0d' };
      return map[t.status] || '#1e1e1e';
    },

    nodeStroke(t) {
      if (!t.amlResult) return '#444';
      return RISK_COLOR[t.amlResult.risk_level] || '#444';
    },

    statusIcon(t) {
      if (t.status === 'confirmed')    return '✓';
      if (t.status === 'failed')       return '✗';
      if (t.status === 'active')       return null;
      if (t.status === 'broadcasting') return '⟳';
      if (t.status === 'blocked')      return '🚫';
      return null;
    },
  },
};
</script>

<style scoped>
.payout-graph {
  width: 100%;
  height: 100%;
  position: relative;
  background: #111;
  border-radius: 8px;
  overflow: hidden;
}
.payout-graph svg { display: block; }

.pg-empty {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #444;
  font-size: .9rem;
  pointer-events: none;
}

/* Pulse animation for source node when flagged */
@keyframes pulse-ring {
  0%   { r: 40; opacity: .8; }
  50%  { r: 46; opacity: .3; }
  100% { r: 40; opacity: .8; }
}
.pulse { animation: pulse-ring 1.8s ease-in-out infinite; }

/* Edge flow animation */
@keyframes dashFlow {
  to { stroke-dashoffset: -24; }
}
.edge-anim { animation: dashFlow 1s linear infinite; }

.target-node { transition: filter .2s; }
.target-node:hover { filter: brightness(1.3); }
.target-node.selected circle:first-child { filter: brightness(1.2); }

.edge-label { pointer-events: none; text-shadow: 0 0 4px #000; }
</style>
